import { uploadFileApi } from "@/api/services/global/file";
import { IconClose } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { ACCEPT_ERROR, FILE_STATUS, TFileUploadFor, MAX_SIZE_ERROR } from "@/constants/file";
import { background, button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import {
  TFileRules,
  checkFileListWhetherIsActualPdf,
  convertMbToByte,
  covertMimeTypesToAccepts,
  getExtensionDetailOfMimeType,
  getFileErrorMessage,
  getFileStatusWithRules,
  getExtensionsOfMimeTypes,
} from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { Box, Grid, IconButton, Stack, Tooltip, Typography, css, styled } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";

export type TDropZoneProps = {
  ref?: React.MutableRefObject<HTMLDivElement | undefined>;
  value?: TExtendFile | TExtendFile[] | null;
  onChange?: (value: TExtendFile | TExtendFile[] | null) => void;
  options?: Omit<DropzoneOptions, "onDrop">;
  enableFileList?: boolean;
  enableFileListCustom?: boolean;
  enableTextFileList?: boolean;
  nonControl?: boolean;
  rules?: TFileRules;
  bordered?: boolean;
  disabled?: boolean;
  uploadFor?: TFileUploadFor;
  hiddenCloseIcon?: boolean;
  hideAfterUpload?: boolean;
  handleFileRejections?: (fileRejections: FileRejection[]) => void;
  childrenCustom?: React.ReactNode | ((index: number) => React.ReactNode);
  childrenRenderHtml?: (args: { open: () => void }) => React.ReactNode;
  onFileRemove?: (removedIndex: number, updatedFiles: TExtendFile[]) => void;
  triggerOpenChosenFile?: (fn: () => void) => void;
  isShowErr?: boolean;
  noClick?: boolean;
  noKeyboard?: boolean;
};

export const DropZone: FCC<TDropZoneProps> = ({
  options = {},
  value,
  onChange,
  enableFileList = true,
  enableFileListCustom = false,
  enableTextFileList = false,
  children,
  rules = {},
  nonControl = false,
  bordered = true,
  hiddenCloseIcon = false,
  disabled,
  uploadFor,
  hideAfterUpload = false,
  childrenCustom,
  childrenRenderHtml,
  handleFileRejections,
  onFileRemove,
  triggerOpenChosenFile,
  isShowErr,
  noClick = false,
  noKeyboard = false,
}) => {
  const { field } = useFormField(nonControl);
  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? value ?? []].flat());
  const [uploading, setUploading] = useState(false);
  const [hideDropZone, setHideDropZone] = useState(false);

  useEffect(() => {
    let incomingFiles: TExtendFile[] = [field?.value ?? value ?? []].flat();

    const areDifferent =
      incomingFiles.length !== files.length || incomingFiles.some((newFile) => !files.some((oldFile) => oldFile.id === newFile.id));

    if (enableTextFileList) {
      incomingFiles =
        incomingFiles.map(file => ({
          ...file,
          name: file.name.includes("_") ? file.name.split("_").slice(1).join("_") : file.name,
        }));
    }

    if (areDifferent) {
      setFiles(incomingFiles);
    }
  }, [value, field]);

  useEffect(() => {
    if (hideAfterUpload && files.length > 0) {
      setHideDropZone(true);
      return;
    }

    setHideDropZone(false);
  }, [files]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (rules?.maxFile && acceptedFiles.length + files.length > rules?.maxFile) {
        let message = `ファイルは最大${rules?.maxFile}件までアップロードできます。`;
        if (rules?.maxFile === 1) {
          message = "1つのファイルしかアップロードできません。";
        }
        toast.error(message);
        return;
      }
      if (rules?.maxFile && acceptedFiles.length + fileRejections.length > rules?.maxFile) {
        let message = `ファイルは最大${rules?.maxFile}件までアップロードできます。`;
        if (rules?.maxFile === 1) {
          message = "1つのファイルしかアップロードできません。";
        }
        toast.error(message);
        return;
      }
      if (fileRejections.length > 0 && handleFileRejections) {
        handleFileRejections(fileRejections);
        return;
      }

      if (rules.acceptFileTypesExtension && rules.acceptFileTypesExtension.length > 0) {
        // Show error message for fileRejections (custom)
        if (fileRejections.length > 0) {
          const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
          if (fileRejections.some((file) => !rules.acceptMimeTypes?.includes(file.file.type))) {
            toast.error(ACCEPT_ERROR(extensions));
            return;
          }
          const maxSize = rules?.maxSize ?? convertMbToByte(10);
          if (fileRejections.some(rej => rej.errors.some(err => err.code === "file-too-large"))) {
            toast.error(MAX_SIZE_ERROR(maxSize));
            return;
          }
        }
      } else {
        if (fileRejections.length > 0 && rules.acceptMimeTypes) {
          const extension = getExtensionDetailOfMimeType(rules.acceptMimeTypes);
          toast.error(ACCEPT_ERROR(extension));
          return;
        }
        if (fileRejections.length > 0 && rules.acceptMimeTypes) {
          const extension = getExtensionDetailOfMimeType(rules.acceptMimeTypes);
          toast.error(ACCEPT_ERROR(extension));
          return;
        }
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      const maxSize = rules?.maxSize ?? convertMbToByte(10);

      for (const file of acceptedFiles) {
        const isValidSize = file.size <= maxSize;

        if (!isValidSize) {
          toast.error(MAX_SIZE_ERROR(maxSize));
          return;
        }
      }
      let newFiles = files;
      setUploading(true);
      const droppedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const fileOption: TExtendFileOption = {
            id: makeUuid(),
            status: getFileStatusWithRules(file, rules, isShowErr),
          };

          if (fileOption.status === FILE_STATUS.OK && uploadFor) {
            try {
              const rs = await uploadFileApi({ file, uploadFor });
              return Object.assign(file, { ...fileOption, filePath: rs.data.filePath });
            } catch (error) {
              showError(error);
              return Object.assign(file, { ...fileOption, status: FILE_STATUS.ERROR });
            }
          }
          return Object.assign(file, fileOption);
        }),
      );

      if (options.multiple) {
        newFiles = [newFiles, droppedFiles].flat();
      } else {
        newFiles = [droppedFiles[0]];
      }
      setFiles(newFiles);
      triggerChangeFiles(newFiles);
      setUploading(false);
    },
    [uploadFor, files],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    ...options,
    onDrop,
    accept: rules.acceptFileTypesExtension && rules.acceptFileTypesExtension.length > 0
      ? { "": rules.acceptFileTypesExtension }
      : covertMimeTypesToAccepts(rules.acceptMimeTypes),
    maxSize: options.maxSize || rules.maxSize,
    disabled: uploading || disabled,
    noClick: noClick,
    noKeyboard: noKeyboard,
  });

  useEffect(() => {
    triggerOpenChosenFile?.(() => open);
  }, [open]);

  const removeFile = (removedId: Key) => {
    const removedIndex = files.findIndex(({ id }) => id === removedId);
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    setFiles(updatedFiles);
    triggerChangeFiles(updatedFiles);
    onChange?.(updatedFiles);
    triggerChangeFiles(updatedFiles);

    if (onFileRemove && removedIndex !== -1) {
      onFileRemove(removedIndex, updatedFiles);
    }
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    const { multiple } = options;
    const valueToSet = multiple === false ? (files.at(0) ?? null) : files;
    onChange?.(valueToSet);
    field?.onChange(valueToSet);
    field?.onBlur();
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    open();
  };

  return (
    <Box>
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        {!hideDropZone && (
          <DropZoneArea dragged={isDragActive} bordered={bordered}>
            {typeof childrenRenderHtml === 'function' ? childrenRenderHtml({ open }) :  children ?? (
              <>
                <Typography variant="body14" color={text.secondary}>
                  アップロードするファイルをドラッグ&ドロップ
                </Typography>
                <Typography variant="body14" color={text.secondary}>
                  または
                </Typography>
                <Button variant="outline" onClick={handleButtonClick}>ファイルを選択する</Button>
              </>
            )}
            {uploading && <UploadingBackup>作成中…</UploadingBackup>}
          </DropZoneArea>
        )}
      </Box>
      {enableFileList && (
        <Stack flexDirection="row" flexWrap="wrap" gap={1} mt={files.length && !hideDropZone ? 1 : 0}>
          {files.map((file) => {
            const fileProps = file.filePath ? { filePath: file.filePath } : { file };
            return (
              <FileItem
                key={file.id}
                {...fileProps}
                downloadable={true}
                onRemove={!hiddenCloseIcon ? () => removeFile(file.id) : undefined}
                maxWidth={336}
                errorMsg={getFileErrorMessage(file, rules)}
              />
            );
          })}
        </Stack>
      )}

      {enableFileListCustom && (
        <Stack mt={files.length && !hideDropZone ? 1 : 0}>
          {files.map((file, index) => {
            const fileProps = file.filePath ? { filePath: file.filePath } : { file };
            return (
              <Box
                key={file.id}
                sx={{
                  display: "grid",
                  alignItems: "center",
                  gap: 3,
                  mt: 2,
                }}
              >
                <FileItem
                  {...fileProps}
                  downloadable={true}
                  onRemove={!hiddenCloseIcon ? () => removeFile(file.id) : undefined}
                  errorMsg={getFileErrorMessage(file, rules)}
                >
                  {typeof childrenCustom === "function" ? childrenCustom(index) : childrenCustom}
                  <Typography variant="cap12" color={text.secondary}>
                    {dayjs(file?.updatedAt).format(YEAR_MONTH_DAY_STR_FORMAT)} 更新
                  </Typography>
                </FileItem>
              </Box>
            );
          })}
        </Stack>
      )}

      {enableTextFileList && (
        <Stack mt={1}>
          {files.map((file, index) => {
            return (
              <Stack mt={1} key={file.id}>
                <Grid container spacing={1} alignItems="flex-start">
                  <Grid item xs={4}>
                    <Typography component="p" variant="cap12">
                      アップロードしたファイル名
                    </Typography>
                    <Tooltip
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: background.white,
                            color: text.primary,
                            boxShadow: "0px 2px 8px 0px #00000040",
                          },
                        },
                        arrow: {
                          sx: {
                            color: background.white,
                          },
                        },
                      }}
                      title={
                        <Typography
                          variant="cap12"
                          sx={{
                            backgroundColor: background.white,
                            color: text.primary,
                          }}
                        >
                          {file.name}
                        </Typography>
                      }
                    >
                      <Typography component="p" variant="body14" mt={1}>
                        {truncateWithEllipsis(file.name, 32)}
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={7.5}>
                    {typeof childrenCustom === "function" ? childrenCustom(index) : childrenCustom}
                  </Grid>
                  <Grid item xs={0.5} mt={3}>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                    >
                      <IconClose fontSize="20px" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const options = { shouldForwardProp: (propName: string) => !["dragged", "bordered"].includes(propName) };

const DropZoneAreaActive = css`
  border-color: ${button.primary};
`;

const DropZoneArea = styled(Stack, options)<{ dragged: boolean; bordered: boolean }>`
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  border: 1px dashed ${divider.middle};
  border-radius: ${rounded.sm};
  overflow: hidden;

  ${({ dragged }) => dragged && DropZoneAreaActive}
  ${({ bordered }) =>
    !bordered &&
    css`
      padding: 0;
      border: none;
    `}
`;

const UploadingBackup = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: ${button.disable};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${text.white};
`;
